import { Injectable } from "@angular/core";
import { JwtHelperService } from "@auth0/angular-jwt";
import { NGXLogger } from "ngx-logger";
import { BillingOrder } from "../models/billingorder.model";
import { Payment } from "../models/payment.model";
import { ScrAuthenticationService } from "./authentication.service";
import { ScrBaseService } from "./base.service";
import { ScrServiceManager } from "./servicemanager.service";
import { DecodedToken } from "./authorization.service";
import { Lease } from "../models/lease.model";
import { DiscountSummary } from "../models/discount.model";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  interface Window { dataLayer: any[]; ngDevMode: boolean; }
}

/**
 * Consolidate feed to GA (and pendo at some point).
 */

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService extends ScrBaseService {

  constructor(
    private authenticationService: ScrAuthenticationService,
    serviceManager: ScrServiceManager,
    private logger: NGXLogger) {
    super(serviceManager);
  }

  emitPaymentForMultiOrderPayment(firstTimePurchase: string | undefined, payment: Payment) {
    this.authenticationService.getIdToken(false).then(token => {
      if (token) {
        const s = new JwtHelperService();
        const decoded = s.decodeToken<DecodedToken>(token);
        if (decoded) {
          const accountId = decoded['custom:accountId'];
          const userId = decoded.userId;

          payment.orders?.forEach(billingOrder => {
            this.emitPaymentForUser(firstTimePurchase, billingOrder, userId, accountId);
            // after the first, make it false.
            firstTimePurchase = 'false';
          });
        }
      }
    });


  }

  emitPayment(firstTimePurchase: string | undefined, billingOrder: BillingOrder) {
    this.authenticationService.getIdToken(false).then(token => {
      if (token) {
        const s = new JwtHelperService();
        const decoded = s.decodeToken<DecodedToken>(token);
        if (decoded) {
          const accountId = decoded['custom:accountId'];
          const userId = decoded.userId;

          this.emitPaymentForUser(firstTimePurchase, billingOrder, userId, accountId);
        }
      }
    });
  }


  emitPaymentForUser(firstTimePurchase: string | undefined, billingOrder: BillingOrder, userId: string, accountId: string) {
    const paymentEvent = {
      "event": "payment",
      "payment_transaction_id": billingOrder.orderId,
      "payment_value": billingOrder.totalPrice,
      "payment_type": billingOrder.paymentMethod ? billingOrder.paymentMethod : 'none',
      "account_id": accountId,
      "user_id": userId,
      "company": billingOrder.company,
      "first_order": firstTimePurchase
    };

    this.logger.trace('emit GA ' + JSON.stringify(paymentEvent));
    window.dataLayer.push(paymentEvent);
  }



  emitPurchase(paidOrNotPaid: string, firstTimePurchase: string | undefined, billingOrder: BillingOrder) {
    this.authenticationService.getIdToken(false).then(token => {
      if (token) {
        const s = new JwtHelperService();
        const decoded = s.decodeToken<DecodedToken>(token);
        if (decoded) {
          const accountId = decoded['custom:accountId'];
          const userId = decoded.userId;
          this.emitPurchaseForUser(paidOrNotPaid, firstTimePurchase, billingOrder, userId, accountId);
        }
      }
    });
  }

  emitLogin(userId?: number, accountId?: number) {
    const e = {
      "event": "login",
      "data": {
        "method": "usernamepassword",
        "user_id": `${userId}`,
        "accountId": `${accountId}`,
        "page_location": window.location.href
      }
    }
    this.logger.trace('emit GA ' + JSON.stringify(e));
    window.dataLayer.push(e);

  }

  emitPurchaseForUser(paidOrNotPaid: string, firstTimePurchase: string | undefined, billingOrder: BillingOrder, userId: string, accountId: string) {

    const purchaseEvent = {
      "event": "purchase",
      "ecommerce": {
        "transaction_id": billingOrder.orderId,
        "affiliation": "USShortCodes", // Static
        "value": billingOrder.totalPrice,
        "tax": "0.00",
        "shipping": "0.00",
        "currency": "USD",
        "payment_status": paidOrNotPaid,
        "payment_type": billingOrder.paymentMethod ? billingOrder.paymentMethod : 'none',
        "account_id": accountId,
        "user_id": userId,
        "company": billingOrder.company,
        "application_type": billingOrder.applicationType?.toLowerCase(),
        "first_order": firstTimePurchase,
        "items": billingOrder.leases.map(item => {
          let coupons: string | undefined = undefined;
          let analyticIds: string | undefined = undefined;
          if (item.discountList) {
            coupons = item.discountList.map(disc => disc.redeemCode).join(', ');
            analyticIds = item.discountList.map(disc => disc.analyticsId).join(', ');
          }
          return {
            "item_name": item.scId?.toString(),
            "item_id": item.scId?.toString(),
            "price": item.price?.toString(),
            "discount": item.discountAmount?.toString(),
            "coupon": coupons,
            "item_brand": "ctia",
            "quantity": item.term,
            "item_category": item.selectType?.toLowerCase(),
            "item_category2": item.forPoliticalCampaign ? 'yes' : 'no',
            "item_category3": item.leaseType === 'BilledMonthly' ? 'Billed Monthly' : item.term?.toString(),
            "item_category4": item.autorenew ? 'enrolled' : 'not enrolled',
            "item_category5": analyticIds
          };
        })
      }
    };
    this.logger.trace('emit GA ' + JSON.stringify(purchaseEvent));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(purchaseEvent);
  }

  private withUserInfo(fct: (userId: string, accountId: string) => void) {
    this.authenticationService.getIdToken(false).then(token => {
      if (token) {
        const s = new JwtHelperService();
        const decoded = s.decodeToken<DecodedToken>(token);
        if (decoded) {
          const accountId = decoded['custom:accountId'];
          const userId = decoded.userId;
          fct(userId, accountId);
        }
      }
    });
  }


  public emitViewItems(leases: Lease[], category?: string) {
    const event = {
      "event": "view_item_list",
      ecommerce: [
        leases.map((l, i) => {
          return {
            currency: 'USD',
            index: i,
            item_category: category,
            item_brand: 'ctia',
            item_id: l.scId,
            item_list_name: l.selectType,
            item_name: l.scId,
            price: l.price,
            quantity: l.term,
            coupon: l.discountList?.map(d => d.redeemCode).join(', ')
          }
        })
      ]
    }
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }


  public emitAddToCart(billingOrder: BillingOrder, scIds?: number[]) {
    if (!scIds) {
      return;
    }
    const filtered = billingOrder.leases.filter(l => scIds.includes(l.scId));
    const total = filtered.reduce((pl, cl) => {
      return pl + cl.price;
    }, 0);
    const event = {
      "event": "add_to_cart",
      ecommerce: {
        currency: 'USD',
        value: total,
        items: [
          filtered.map((l) => {
            return {
              currency: 'USD',
              index: l.positionInItemList ?? 0,
              item_brand: 'ctia',
              item_id: l.scId,
              item_list_name: l.selectType,
              item_name: l.scId,
              price: `${l.price}`,
              quantity: l.term
            }
          })
        ]
      }
    }
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }


  public emitRemoveFromCart(lease: Lease) {
    const event = {
      "event": "remove_from_cart",
      ecommerce: {
        currency: 'USD',
        value: lease.price,
        items: [
          {
            currency: 'USD',
            index: lease.positionInItemList ?? 0,
            item_brand: 'ctia',
            item_id: lease.scId,
            item_list_name: lease.selectType,
            item_name: lease.scId,
            price: `${lease.price}`,
            quantity: lease.term
          }
        ]
      }
    }
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }


  emitSelectPromotion(billingOrder: BillingOrder, promotionsApplied: DiscountSummary[]) {
    const applied = promotionsApplied.map(p => p.discountId);
    const leasesWithDiscount = billingOrder.leases.filter(lease => {
      const leaseDiscounts = (lease.discountList ?? []).map(d => d.discountId);
      return leaseDiscounts.some(d => applied.includes(d));
    });
    const event = {
      "event": "select_promotion",
      "ecommerce": {
        "items": leasesWithDiscount.map(item => {
          let coupons: string | undefined = undefined;
          let analyticIds: string | undefined = undefined;
          if (item.discountList) {
            coupons = item.discountList.map(disc => disc.redeemCode).join(', ');
            analyticIds = item.discountList.map(disc => disc.analyticsId).join(', ');
          }
          return {
            "item_name": item.scId?.toString(),
            "item_id": item.scId?.toString(),
            "price": item.price?.toString(),
            "discount": item.discountAmount?.toString(),
            "coupon": coupons,
            "item_brand": "ctia",
            "quantity": item.term,
            "item_category": item.selectType?.toLowerCase(),
            "item_category2": item.forPoliticalCampaign ? 'yes' : 'no',
            "item_category3": item.leaseType === 'BilledMonthly' ? 'Billed Monthly' : item.term?.toString(),
            "item_category4": item.autorenew ? 'enrolled' : 'not enrolled',
            "item_category5": analyticIds
          };
        })
      }
    };
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }

  /**
   * TODO when they view the cart.
   * 
   * @param billingOrder 
   */
  public emitViewCart(billingOrder: BillingOrder) {

    const total = billingOrder.leases.reduce((pl, cl) => {
      return pl + cl.price;
    }, 0);

    const event = {
      "event": "view_cart",
      ecommerce: {
        currency: 'USD',
        value: total,
        items: [
          billingOrder.leases.map((l,idx) => {
            return {
              currency: 'USD',
              index: l.positionInItemList ?? idx,
              item_brand: 'ctia',
              item_id: l.scId,
              item_list_name: l.selectType,
              item_name: l.scId,
              price: `${l.price}`,
              quantity: l.term,
              coupon: l.discountList?.map(d => d.redeemCode).join(', '),
              discount: l.discountAmount?.toString()
            }
          })
        ]
      }
    }
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }


  /**
   * TODO when they leave the cart and move to the next whatever that is.
   * 
   * @param billingOrder 
   */
  public emitViewBeginCheckout(billingOrder: BillingOrder) {
    const total = billingOrder.leases.reduce((pl, cl) => {
      return pl + cl.price;
    }, 0);

    const event = {
      "event": "begin_checkout",
      ecommerce: {
        currency: 'USD',
        value: total,
        items: [
          billingOrder.leases.map((l,idx) => {
            return {
              currency: 'USD',
              index: l.positionInItemList ?? idx,
              item_brand: 'ctia',
              item_id: l.scId,
              item_list_name: l.selectType,
              item_name: l.scId,
              price: `${l.price}`,
              quantity: l.term,
              coupon: l.discountList?.map(d => d.redeemCode).join(', '),
              discount: l.discountAmount?.toString()
            }
          })
        ]
      }
    }
    this.logger.trace('emit GA ' + JSON.stringify(event));
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(event);
  }
}